/**
 * Auth-Layer ueber PocketBase. Email + Password.
 *
 * Public API:
 *  - `getSession()` / `getUserId()` / `isAuthenticated()` — Snapshot/Helper
 *  - `subscribe(cb)` — React useSyncExternalStore-kompatibel
 *  - `signIn(email, password)` — loggt ein, Session bleibt persistent
 *  - `signOut()` — beendet Session
 *
 * Im Anonym-Modus (kein PocketBase konfiguriert) liefert `getSession()` immer `null`
 * und alle Operationen werfen `CloudNotConfiguredError`.
 *
 * Auf OTP / Magic-Link kann spaeter umgestellt werden, sobald ein SMTP-Provider
 * konfiguriert ist — siehe `users`-Collection-Settings im PocketBase-Admin.
 */

import type { AuthRecord } from 'pocketbase';
import { getPocketBase, isCloudConfigured } from './pocketbaseClient';

export class CloudNotConfiguredError extends Error {
  constructor() {
    super('Cloud-Sync ist nicht konfiguriert (VITE_POCKETBASE_URL fehlt).');
    this.name = 'CloudNotConfiguredError';
  }
}

export interface AuthSession {
  userId: string;
  email: string;
}

interface AuthState {
  session: AuthSession | null;
  initialized: boolean;
}

const state: AuthState = {
  session: null,
  initialized: false,
};

const listeners = new Set<() => void>();

function notify(): void {
  for (const cb of listeners) cb();
}

function recordToSession(record: AuthRecord): AuthSession | null {
  if (!record) return null;
  const id = record['id'];
  const email = record['email'];
  if (typeof id !== 'string' || typeof email !== 'string') return null;
  return { userId: id, email };
}

function ensureInitialized(): void {
  if (state.initialized) return;
  state.initialized = true;
  const pb = getPocketBase();
  if (!pb) return;
  if (pb.authStore.isValid) {
    state.session = recordToSession(pb.authStore.record);
  }
  pb.authStore.onChange(() => {
    state.session = pb.authStore.isValid ? recordToSession(pb.authStore.record) : null;
    notify();
  });
}

if (isCloudConfigured()) {
  ensureInitialized();
}

export function getSession(): AuthSession | null {
  return state.session;
}

export function getUserId(): string | null {
  return state.session?.userId ?? null;
}

export function isAuthenticated(): boolean {
  return state.session !== null;
}

export function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

export interface SignInResult {
  ok: boolean;
  error?: string;
}

export async function signIn(email: string, password: string): Promise<SignInResult> {
  const pb = getPocketBase();
  if (!pb) throw new CloudNotConfiguredError();
  const trimmed = email.trim().toLowerCase();
  if (!trimmed.includes('@')) {
    return { ok: false, error: 'Bitte gueltige E-Mail-Adresse eingeben.' };
  }
  if (password.length === 0) {
    return { ok: false, error: 'Bitte Passwort eingeben.' };
  }
  try {
    await pb.collection('users').authWithPassword(trimmed, password);
    return { ok: true };
  } catch (err) {
    return { ok: false, error: extractErrorMessage(err) };
  }
}

export function signOut(): void {
  const pb = getPocketBase();
  if (!pb) throw new CloudNotConfiguredError();
  pb.authStore.clear();
}

function extractErrorMessage(err: unknown): string {
  if (err && typeof err === 'object') {
    const status = (err as { status?: unknown }).status;
    if (status === 400) {
      return 'E-Mail oder Passwort falsch.';
    }
  }
  if (err instanceof Error) {
    return err.message;
  }
  return 'Unerwarteter Fehler.';
}

/** Test-Helper. NICHT im App-Code verwenden. */
export function __resetAuthForTests(): void {
  state.session = null;
  state.initialized = false;
  listeners.clear();
}
